/**
 * The optional "How did you hear about us?" answer on a signup form.
 *
 * Never required. A missing, unknown, or oversized value is dropped or
 * clipped, and the account is still created. The choice is a stable code;
 * the words a person sees live in the form's own language.
 */

export const HEARD_ABOUT_CHOICES = [
  'google_search',
  'youtube',
  'facebook_instagram',
  'friend_or_cleaner',
  'php_point_of_sale',
  'indie_hackers_or_reddit',
  'other',
] as const;

export type HeardAboutChoice = (typeof HEARD_ABOUT_CHOICES)[number];

/** The free-text box next to "Other". Longer answers are clipped, not refused. */
export const HEARD_ABOUT_DETAIL_LIMIT = 200;

/**
 * English labels for the operator console and the signup notification.
 * The signup form itself is translated; this list is not.
 */
export const HEARD_ABOUT_LABELS: Record<HeardAboutChoice, string> = {
  google_search: 'Google search',
  youtube: 'YouTube',
  facebook_instagram: 'Facebook or Instagram',
  friend_or_cleaner: 'A friend or another cleaner',
  php_point_of_sale: 'PHP Point Of Sale',
  indie_hackers_or_reddit: 'Indie Hackers or Reddit',
  other: 'Other',
};

export function isHeardAboutChoice(value: string): value is HeardAboutChoice {
  return (HEARD_ABOUT_CHOICES as readonly string[]).includes(value);
}

/** The words Chris should read. An unknown code is shown as stored. */
export function heardAboutLabel(choice: string | null | undefined): string | null {
  if (!choice) return null;
  return isHeardAboutChoice(choice) ? HEARD_ABOUT_LABELS[choice] : choice;
}

/**
 * The pair worth storing. Detail is kept only for "Other" — a note attached
 * to Google search would be an answer the form did not ask for.
 */
export function pairHeardAbout(
  choice: HeardAboutChoice | null | undefined,
  detail: string | null | undefined,
): { choice: HeardAboutChoice; detail: string | null } | null {
  if (!choice) return null;
  if (choice !== 'other') return { choice, detail: null };
  const trimmed = detail?.trim() ?? '';
  return { choice, detail: trimmed ? trimmed.slice(0, HEARD_ABOUT_DETAIL_LIMIT) : null };
}
